import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const CurrencyContext = createContext(null);
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STORAGE_KEY = 'baobab_currency';
const SUGGESTION_KEY = 'baobab_currency_suggested';

export const useCurrency = () => {
  const ctx = useContext(CurrencyContext);
  if (!ctx) throw new Error('useCurrency must be used within CurrencyProvider');
  return ctx;
};

export const CurrencyProvider = ({ children }) => {
  const { user } = useAuth();
  const [secondaryCurrency, setSecondaryCurrencyState] = useState(() => {
    return localStorage.getItem(STORAGE_KEY) || 'fmg';
  });
  const [suggestion, setSuggestion] = useState(null); // { currency: 'eur'|'usd' }
  const [suggestionDismissed, setSuggestionDismissed] = useState(() => {
    return localStorage.getItem(SUGGESTION_KEY) === 'true';
  });

  // Sync from user profile on login
  useEffect(() => {
    if (user?.currency_preference) {
      setSecondaryCurrencyState(user.currency_preference);
      localStorage.setItem(STORAGE_KEY, user.currency_preference);
    }
  }, [user]);

  // Detect location + language on first visit
  useEffect(() => {
    if (suggestionDismissed) return;
    if (localStorage.getItem(STORAGE_KEY)) return; // User already chose

    const detect = async () => {
      try {
        const { data } = await axios.get(`${API}/geo/detect`, { timeout: 5000 });
        const country = data.country_code || '';
        if (country === 'MG') return; // Madagascar — keep FMG, no suggestion

        const lang = localStorage.getItem('i18nextLng') || localStorage.getItem('language') || 'fr';
        if (lang === 'fr') {
          setSuggestion({ currency: 'eur' });
        } else if (lang === 'en') {
          setSuggestion({ currency: 'usd' });
        }
      } catch {
        // Geo detection failed — no suggestion
      }
    };
    detect();
  }, [suggestionDismissed]);

  const setCurrency = useCallback(async (currency) => {
    setSecondaryCurrencyState(currency);
    localStorage.setItem(STORAGE_KEY, currency);
    // Persist to profile if logged in
    if (user) {
      try {
        await axios.put(`${API}/auth/currency-preference`, { currency }, { withCredentials: true });
      } catch (error) { console.error('Currency preference save failed:', error); }
    }
  }, [user]);

  const acceptSuggestion = useCallback(() => {
    if (suggestion) {
      setCurrency(suggestion.currency);
    }
    setSuggestion(null);
    setSuggestionDismissed(true);
    localStorage.setItem(SUGGESTION_KEY, 'true');
  }, [suggestion, setCurrency]);

  const dismissSuggestion = useCallback(() => {
    setSuggestion(null);
    setSuggestionDismissed(true);
    localStorage.setItem(SUGGESTION_KEY, 'true');
  }, []);

  return (
    <CurrencyContext.Provider value={{
      secondaryCurrency,
      setCurrency,
      suggestion,
      acceptSuggestion,
      dismissSuggestion,
    }}>
      {children}
    </CurrencyContext.Provider>
  );
};
